import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useToast } from '../contexts/ToastContext';

export function useAdminDashboard() {
  const [data, setData] = useState({
    totalUsers: 0,
    freeUsers: 0,
    proUsers: 0,
    totalProjectGroups: 0,
    totalProjects: 0,
    totalTasks: 0,
    freeUserStats: {
      projectGroups: 0,
      projects: 0,
      tasks: 0
    },
    proUserStats: {
      projectGroups: 0,
      projects: 0,
      tasks: 0
    },
    recentUsers: [],
    userGrowthData: [],
    subscriptionBreakdown: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const addToast = useToast();

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 AdminDashboard: Starting data fetch...');

      // Get total users count from user_profiles
      const { count: totalUsers, error: usersError } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true });

      console.log('👥 AdminDashboard: Total users:', totalUsers);
      if (usersError) {
        console.error('❌ AdminDashboard: Users error:', usersError);
        throw usersError;
      }

      // Get subscription data (might be empty if no subscriptions exist yet)
      const { data: subscriptionData, error: subError } = await supabase
        .from('user_subscriptions')
        .select(`
          user_id,
          status,
          created_at,
          subscription_tiers (
            name,
            display_name
          )
        `)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      // Don't throw error if subscriptions table is empty or doesn't exist
      if (subError && subError.code !== 'PGRST116') {
        console.warn('Could not fetch subscription data:', subError);
      }

      // Get recent users
      const { data: recentUsersData, error: recentError } = await supabase
        .from('user_profiles')
        .select('user_id, first_name, last_name, username, created_at')
        .order('created_at', { ascending: false })
        .limit(10);

      if (recentError) throw recentError;

      // Calculate user counts (assume all users are free if no subscription data)
      const activeSubscriptions = subscriptionData || [];
      const proUserIds = activeSubscriptions
        .filter(sub => sub.subscription_tiers?.name === 'pro')
        .map(sub => sub.user_id);
      
      const proUsers = proUserIds.length;
      const freeUsers = (totalUsers || 0) - proUsers;

      // Get content statistics
      const [
        { count: totalProjectGroups },
        { count: totalProjects },
        { count: totalTasks }
      ] = await Promise.all([
        supabase.from('project_groups').select('*', { count: 'exact', head: true }),
        supabase.from('projects').select('*', { count: 'exact', head: true }),
        supabase.from('tasks').select('*', { count: 'exact', head: true })
      ]);

      // Get user-specific stats
      const freeUserIds = recentUsersData?.filter(user => 
        !proUserIds.includes(user.user_id)
      ).map(user => user.user_id) || [];

      // Get stats for free users
      let freeUserStats = { projectGroups: 0, projects: 0, tasks: 0 };
      if (freeUserIds.length > 0) {
        const [
          { count: freePG },
          { count: freeProj },
          { count: freeTasks }
        ] = await Promise.all([
          supabase.from('project_groups').select('*', { count: 'exact', head: true }).in('user_id', freeUserIds),
          supabase.from('projects').select('*', { count: 'exact', head: true }).in('user_id', freeUserIds),
          supabase.from('tasks').select('*', { count: 'exact', head: true }).in('user_id', freeUserIds)
        ]);

        freeUserStats = {
          projectGroups: freePG || 0,
          projects: freeProj || 0,
          tasks: freeTasks || 0
        };
      }

      // Get stats for pro users
      let proUserStats = { projectGroups: 0, projects: 0, tasks: 0 };
      if (proUserIds.length > 0) {
        const [
          { count: proPG },
          { count: proProj },
          { count: proTasks }
        ] = await Promise.all([
          supabase.from('project_groups').select('*', { count: 'exact', head: true }).in('user_id', proUserIds),
          supabase.from('projects').select('*', { count: 'exact', head: true }).in('user_id', proUserIds),
          supabase.from('tasks').select('*', { count: 'exact', head: true }).in('user_id', proUserIds)
        ]);

        proUserStats = {
          projectGroups: proPG || 0,
          projects: proProj || 0,
          tasks: proTasks || 0
        };
      }

      // Create user growth data (last 30 days)
      const userGrowthData = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        const usersUpToDate = recentUsersData?.filter(u => 
          new Date(u.created_at) <= date
        ).length || 0;
        
        userGrowthData.push({
          date: dateStr,
          users: usersUpToDate,
          day: date.toLocaleDateString('en-US', { weekday: 'short' })
        });
      }

      // Create subscription breakdown
      const subscriptionBreakdown = [
        { name: 'Free', value: freeUsers, color: '#ef4444' },
        { name: 'Pro', value: proUsers, color: '#22c55e' }
      ];

      // Set the dashboard data
      const dashboardData = {
        totalUsers: totalUsers || 0,
        freeUsers,
        proUsers,
        totalProjectGroups: totalProjectGroups || 0,
        totalProjects: totalProjects || 0,
        totalTasks: totalTasks || 0,
        freeUserStats,
        proUserStats,
        recentUsers: recentUsersData || [],
        userGrowthData,
        subscriptionBreakdown
      };
      
      console.log('📊 AdminDashboard: Final dashboard data:', dashboardData);
      setData(dashboardData);

    } catch (err) {
      console.error('❌ AdminDashboard: Error fetching dashboard data:', err);
      setError(err.message);
      addToast('Error loading dashboard data', 'error');
    } finally {
      console.log('✅ AdminDashboard: Fetch complete, setting loading to false');
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    data,
    loading,
    error,
    refetch: fetchDashboardData
  };
}
